import { AlertTriangle, PhoneOff, DollarSign, ArrowRight, Brain, Wrench, BarChart3 } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

export default function ProblemSection() {
  const { ref, isVisible } = useScrollReveal(0.15);

  return (
    <section className="pt-4 md:pt-6 pb-10 md:pb-14 px-4" ref={ref}>
      <div className={`max-w-5xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Problem row */}
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-xl md:text-3xl font-extrabold mb-2">Sound familiar?</h2>
          <p className="text-white/40 text-xs md:text-sm">Every private gym owner has lived this.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-10 items-stretch">
          <PainCard icon={<AlertTriangle className="w-6 h-6" />} title="Equipment goes down" desc="Friday afternoon. Tech can't come until Thursday. Members leave 1-star reviews." />
          <PainCard icon={<PhoneOff className="w-6 h-6" />} title="Useless support" desc="40 minutes on hold. Transferred twice. Someone reads from a script." />
          {/* Price-tag pain card — the third tile gets a custom layout so the
              dollar range hits visually the way the other two cards' headlines do. */}
          <div className="relative flex flex-col p-4 md:p-5 rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-amber-600/5 hover:from-amber-500/15 hover:to-amber-600/10 hover:border-amber-500/30 transition-all duration-200 hover:translate-y-[-1px] overflow-hidden">
            <div className="text-amber-400 mb-2"><DollarSign className="w-6 h-6" /></div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-2xl md:text-3xl font-extrabold text-amber-300 tracking-tight">$200<span className="text-amber-400/60">–</span>$600</span>
            </div>
            <p className="text-amber-300/70 text-[10px] font-bold uppercase tracking-wider mb-2">per diagnostic call</p>
            <p className="text-white/55 text-xs leading-relaxed">Tech is there 30 minutes. Fixes something you could've done in 20 — once you knew the trick.</p>
          </div>
        </div>

        {/* Transition arrow */}
        <div className="flex justify-center mb-6 md:mb-8">
          <div className="flex items-center gap-2 text-blue-400">
            <div className="h-px w-12 bg-blue-400/30" />
            <ArrowRight className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">AthnxGym changes this</span>
            <ArrowRight className="w-5 h-5" />
            <div className="h-px w-12 bg-blue-400/30" />
          </div>
        </div>

        {/* Solution row */}
        <div className="grid md:grid-cols-3 gap-3 md:gap-4">
          <SolutionCard icon={<Brain className="w-6 h-6" />} title="Know Your Equipment" desc="AI diagnostics, manufacturer playbooks, and parts guides." />
          <SolutionCard icon={<Wrench className="w-6 h-6" />} title="Prevent the Breakdown" desc="PM calendars generated from your inventory." />
          <SolutionCard icon={<BarChart3 className="w-6 h-6" />} title="Run It Like a Business" desc="Health tracking, staff performance, and real support." />
        </div>
      </div>
    </section>
  );
}

function PainCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="p-4 md:p-5 rounded-xl border border-amber-500/10 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/20 transition-all duration-200 hover:translate-y-[-1px]">
      <div className="text-amber-400 mb-2">{icon}</div>
      <h3 className="text-sm font-bold mb-1 text-amber-300">{title}</h3>
      <p className="text-white/50 text-xs leading-relaxed">{desc}</p>
    </div>
  );
}

function SolutionCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="p-4 md:p-5 rounded-xl border border-blue-500/10 bg-blue-500/5 hover:bg-blue-500/10 hover:border-blue-500/20 transition-all duration-200 hover:translate-y-[-1px]">
      <div className="text-blue-400 mb-2">{icon}</div>
      <h3 className="text-sm font-bold mb-1">{title}</h3>
      <p className="text-white/50 text-xs leading-relaxed">{desc}</p>
    </div>
  );
}
